import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAccount, usePublicClient, useSendTransaction, useWriteContract, useSwitchChain } from "wagmi";
import { encodeDeployData, erc20Abi } from "viem";
import { ArrowLeft, Trophy, Rocket, Coins, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import pot from "../lib/potArtifact.json";
import { api, errMsg } from "../lib/api";
import { PHASE, fmtAmt } from "../lib/competition";
import { addrUrl } from "../lib/chains";
import { useApp } from "../context/AppContext";
import { Panel, Loading, ErrorState } from "../components/State";
import { TxStatus, runTx } from "../components/TxStatus";
import { CompetitionForm } from "../components/competition/CompetitionForm";
import { FeeSettings } from "../components/competition/FeeSettings";
import { useTokenMetadata } from "../components/TokenVisibility";

const useChainTx = () => {
  const { chainId } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const pc = usePublicClient({ chainId: 4663 });
  const [tx, setTx] = useState(null);
  const ensure = async () => { if (chainId !== 4663) await switchChainAsync({ chainId: 4663 }); };
  return { pc, tx, setTx, ensure };
};

const DeployStep = ({ c, onDone }) => {
  const { pc, tx, setTx, ensure } = useChainTx();
  const { sendTransactionAsync } = useSendTransaction();
  const go = async () => {
    try {
      await ensure();
      const [token, signer, s, e, n] = c.deploy.args;
      const data = encodeDeployData({ abi: pot.abi, bytecode: pot.bytecode, args: [token, signer, BigInt(s), BigInt(e), n] });
      const rc = await runTx({ pc, setTx, chainId: 4663, label: "Deploy reward pot", send: () => sendTransactionAsync({ chainId: 4663, data }) });
      if (rc) { await api.post(`/competitions/${c.id}/deployed`, { tx_hash: rc.transactionHash }); toast.success("Pot deployed"); onDone(); }
    } catch (err) { toast.error(errMsg(err)); }
  };
  return <div className="space-y-2"><button data-testid={`comp-deploy-${c.id}`} onClick={go} className="btn-lime px-4 py-2 text-xs"><Rocket className="h-3.5 w-3.5" /> Step 1 · Deploy reward pot</button><TxStatus tx={tx} testid={`comp-deploy-tx-${c.id}`} /></div>;
};

const FundStep = ({ c, onDone }) => {
  const { pc, tx, setTx, ensure } = useChainTx();
  const { writeContractAsync } = useWriteContract();
  const check = async () => { try { const { data } = await api.post(`/competitions/${c.id}/check-funding`); data.phase === "awaiting_funding" ? toast.message("Pot not fully funded yet") : toast.success("Pot funded — competition scheduled"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  const go = async () => {
    try {
      await ensure();
      const rc = await runTx({ pc, setTx, chainId: 4663, label: `Send ${c.token_info?.symbol} to pot`, send: () => writeContractAsync({ chainId: 4663, address: c.token, abi: erc20Abi, functionName: "transfer", args: [c.pot, BigInt(c.reward_amount)] }) });
      if (rc) await check();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <button data-testid={`comp-fund-${c.id}`} onClick={go} className="btn-lime px-4 py-2 text-xs"><Coins className="h-3.5 w-3.5" /> Step 2 · Send {fmtAmt(c.reward_amount, c.token_info?.decimals)} {c.token_info?.symbol} to the pot</button>
        <button data-testid={`comp-check-funding-${c.id}`} onClick={check} className="rounded-lg border border-white/15 px-3 text-xs text-slate-300 hover:text-lime"><RefreshCw className="inline h-3 w-3" /> Check deposit</button>
      </div>
      <p className="text-[10px] text-slate-500">Once sent, tokens are locked until payout. Unpaid pots can be reclaimed 30 days after the end.</p>
      <TxStatus tx={tx} testid={`comp-fund-tx-${c.id}`} />
    </div>
  );
};

const AdminCompRow = ({ c, reload }) => {
  const [label, tone] = PHASE[c.phase] || PHASE.ended;
  const cancel = async () => { if (!window.confirm("Cancel this competition?")) return; try { await api.delete(`/competitions/${c.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <li className="py-4 space-y-3" data-testid={`admin-comp-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <b className="text-white">{c.title}</b>
        <span className={`text-[10px] font-bold uppercase ${tone}`} data-testid={`admin-comp-phase-${c.id}`}>{label}</span>
        <span className="text-xs text-slate-500">{new Date(c.start_ts * 1000).toLocaleString()} → {new Date(c.end_ts * 1000).toLocaleString()} · top {c.winners} · {fmtAmt(c.reward_amount, c.token_info?.decimals)} {c.token_info?.symbol}</span>
        {c.pot && <a href={addrUrl(c.pot)} target="_blank" rel="noreferrer" className="text-xs text-lime hover:underline">pot</a>}
        {["draft", "awaiting_funding"].includes(c.phase) && <button data-testid={`admin-comp-cancel-${c.id}`} onClick={cancel} className="ml-auto text-xs text-slate-500 hover:text-danger"><X className="inline h-3 w-3" /> Cancel</button>}
      </div>
      {c.is_owner && c.phase === "draft" && <DeployStep c={c} onDone={reload} />}
      {c.is_owner && c.phase === "awaiting_funding" && <FundStep c={c} onDone={reload} />}
    </li>
  );
};

export default function TokenAdmin() {
  const { address } = useParams();
  const { user } = useApp();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get(`/competitions/token/${address}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))), [address]);
  useEffect(() => { load(); }, [load, user?.address]);
  const meta = useTokenMetadata(address);
  return (
    <div className="mx-auto max-w-5xl px-3 lg:px-6 py-6 space-y-5" data-testid="token-admin-page">
      <Link to={`/token/${address}`} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-lime" data-testid="token-admin-back"><ArrowLeft className="h-3 w-3" /> Back to token page</Link>
      <h1 className="font-display text-3xl sm:text-4xl font-black text-white">Token admin {meta?.symbol && <span className="text-lime">${meta.symbol}</span>}</h1>
      {err ? <ErrorState message={err} /> : !d ? <Loading /> : !user ? <ErrorState testid="token-admin-signin" message="Connect and sign in with the token's creator wallet to manage it." />
        : !d.can_manage ? <ErrorState testid="token-admin-forbidden" message="Only this token's creator (or its approved lister) can open the admin." /> : (<>
          <FeeSettings address={address} />
          <Panel title="New trading competition" icon={Trophy} testid="comp-create-panel"><CompetitionForm token={address} onCreated={load} /></Panel>
          <Panel title="Your competitions" testid="admin-comp-list">
            <ul className="px-5 divide-y divide-white/5">
              {!d.competitions.length && <li className="py-6 text-xs text-slate-500">No competitions yet.</li>}
              {d.competitions.map((c) => <AdminCompRow key={c.id} c={c} reload={load} />)}
            </ul>
          </Panel>
        </>)}
    </div>
  );
}

